import { combineReducers, Reducer } from "@reduxjs/toolkit"
import { AnyAction } from "@nova/constants"
import { userLogoutAction } from "user/authorization/actions"
import decisionReducer from "features/content-overlay/decision/decisionSlice"
import overlayReducer from "features/content-overlay/slice"
import mapReducers from "features/map/reducers"
import entitiesReducers from "features/entity-caching/reducers"
import loadingReducer from "features/loading-suspension/loadingSlice"
import panelsReducer from "panels/state/slice"
import userReducer from "user/reducers"
import exportsReducer from "features/data-management/exports/slice"
import systemsReducer from "features/systems-management/slice"
import runlevelReducers from "runlevel/slice"
import ignoredBanners from "features/banners/slice"
// @todo - move this into a plugin to avoid hardcoding against the global state
import { uiReducer as InfraredImageUI } from "plugins/InfraredImage/InfraredImageSlice"
import { uiReducer as OrthoUI } from "plugins/Orthomosaic/OrthomosaicSlice"
import { uiReducer as OrthoComparisonUI } from "plugins/Orthomosaic/OrthoComparisonSlice"
import { uiReducer as VideoUI } from "plugins/Video/FMV/VideoSlice"
import { uiReducer as ColorFinderUI } from "plugins/ColorFinder/VideoColorFinder/ColorFinderSlice"
import { uiReducer as ImageColorFinderUI } from "plugins/ColorFinder/ImageColorFinder/ImageColorFinderSlice"
import { uiReducer as PanoramaUI } from "plugins/Panorama/PanoramaSlice"
import { uiReducer as IconImageUI } from "plugins/Toolbox/Icons/IconSlice"
import { reducer as PortfolioManagement } from "features/data-management/workspace/WorkspaceManagementSlice"
import { extraDataReducer as PDFExtraDataReducer, dataReducer } from "plugins/PDFv2/PDFSlice"
import { uiReducer as SidePanelReducerOld } from "panels/content/side-panel/slice"
import { reducer as UserManagement } from "features/entity-caching/members/UserManagementSlice"
import { reducer as ToolboxReducer } from "plugins/Toolbox/tool-registry"
import { reducer as TextReducer } from "plugins/Toolbox/Text/slice"
import { reducer as LineReducer } from "plugins/Toolbox/Line/LineSlice"
import { reducer as RulerReducer } from "plugins/Toolbox/Ruler/slice"
import { receiveState, reducer as pubsubReducer } from "features/pubsub/slice"
import { combineStates } from "./combineStates"
import { reducer as VideoReducer } from "@nova/slice-video"
import { reducer as ColorFinderReducer } from "@nova/slice-color-finder"
import { loadProject } from "features/map/control/thunks"
import { Project } from "@nova/protobuf"
import { reducer as projectReducer } from "features/entity-caching/project/slice"
import { uiReducer as PDFRasterUI } from "plugins/PDFRaster/PDFRasterSlice"
import { licensesSliceReducer } from "features/entity-caching/licenses/slice"
import { UnionToTuple } from "@nova/serializer/src/client/validators/util"
import { uiReducer as InspectorPanelReducer } from "features/map/inspector-panel/slice"
import { reducer as firePerimeterReducer } from "plugins/DrawPerimeter/FirePerimeterSlice"
import { reducer as orthoMaskReducer } from "plugins/Orthomosaic/OrthoMaskSlice"
import { uiReducer as sharedGeometryReducer } from "plugins/shared/geometry/SharedGeometrySlice"
import { reducer as firePerimeterBuffersReducer } from "features/entity-caching/fire-perimeters/buffers/slice"
import { HotspotPanelReducer } from "panels/hotspot/slice"
import { SidePanelReducer } from "panels/content/side-panel/slice.new"
import { HotspotMultiSelectReducer } from "features/data-management/hotspots/slice"
import { PopupsReducer, popupsSliceName } from "features/popups/slice"
import {
    SatelliteOverlapReducer,
    satelliteOverlapSliceName,
} from "plugins/Satellite/SatelliteOverlapSlice"
import { setNewViewstate } from "features/map/GlobalViewState"
import { ColorFinderV2Reducer } from "plugins/ColorFinder/V2/redux/slice"
import { PilotTrackingReducer } from "features/pilot-tracking/redux/slice"
import { videoSliceName, videoSliceReducer } from "plugins/Video/state/slice"
import {
    userLocationShareSliceName,
    userLocationShareSliceReducer,
} from "plugins/UserLocationShare/state/slice"
import { playbackSliceName, playbackSliceReducer } from "plugins/shared/playback/state/slice"
import { GlobalNavReducer, globalNavSliceName } from "panels/global-nav/slice"
import {
    configurationSliceName,
    ConfigurationSliceReducer,
} from "features/data-management/workspace/workspace/body/settings-and-collaborators/configuration/slice"
import {
    profileSliceName,
    ProfileSliceReducer,
} from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/slice"
import {
    passwordSliceName,
    PasswordSliceReducer,
} from "features/data-management/workspace/workspace/body/settings-and-collaborators/password/slice"
import {
    notificationSettingsSliceName,
    NotificationSettingsSliceReducer,
} from "panels/notifications/settings/slice"
import {
    licensesSettingsSliceName,
    LicensesSettingsSliceReducer,
} from "features/data-management/workspace/workspace/body/settings-and-collaborators/licenses/slice"
import {
    MissionsTableReducer,
    missionsTableSliceName,
} from "features/pilot-tracking/redux/missions-table-slice"
import { aircraftSliceName, AircraftSliceReducer } from "features/pilot-tracking/aircraft/slice"
import {
    checklistCreationSliceName,
    ChecklistCreationSliceReducer,
} from "features/pilot-tracking/checklists/slice"
import { pilotsSliceName, PilotsSliceReducer } from "features/pilot-tracking/pilots/slice"
import { activitySliceName, ActivitySliceReducer } from "features/activity/slice"
import {
    maintenanceTemplateFormSliceName,
    maintenanceTemplateFormReducer,
} from "features/pilot-tracking/maintenance/slice"
import {
    ChecklistDetailsReducer,
    checklistDetailsSliceName,
} from "features/pilot-tracking/checklists/checklist-details/slice"
import { OverviewReducer, overviewSliceName } from "features/overview/slice"
import {
    missionGroupsSliceName,
    MissionGroupsSliceReducer,
} from "features/data-management/workspace/workspace/body/settings-and-collaborators/mission-groups/slice"
import {
    currencyPolicyFormSliceName,
    currencyPolicyFormReducer,
} from "features/pilot-tracking/currency/slice"
import { uiReducer as mapCommentReducer } from "plugins/Toolbox/MapComments/CommentSlice"

const applicationReducers = combineReducers({
    user: userReducer,
    loading: loadingReducer,
    decision: decisionReducer,
})

const mapStateReducers = {
    overlay: overlayReducer,
    runlevel: runlevelReducers,
    map: mapReducers,
    // contains state values that control the ui behavior of all panels (control boxes rendered on top of maps)
    panels: panelsReducer,
    // controls how the content is displayed in a modal/overlay type scenario
    ...SidePanelReducerOld,
    ...InspectorPanelReducer,
    ...PDFExtraDataReducer,
    "pdf-data": dataReducer,
    ...VideoUI,
    ...TextReducer,
    ...LineReducer,
    ...RulerReducer,
    ...firePerimeterReducer,
    ...orthoMaskReducer,
    ...ToolboxReducer,
    ...InfraredImageUI,
    ...OrthoUI,
    ...OrthoComparisonUI,
    ...VideoReducer,
    ...ColorFinderUI,
    ...ImageColorFinderUI,
    ...ColorFinderReducer,
    ...PanoramaUI,
    ...IconImageUI,
    ...mapCommentReducer,
    [videoSliceName]: videoSliceReducer,
    [userLocationShareSliceName]: userLocationShareSliceReducer,
    [playbackSliceName]: playbackSliceReducer,
    ...sharedGeometryReducer,
    ...firePerimeterBuffersReducer,
    ...HotspotPanelReducer,
    ...SidePanelReducer,
    ...HotspotMultiSelectReducer,
    [popupsSliceName]: PopupsReducer,
    [satelliteOverlapSliceName]: SatelliteOverlapReducer,
    [missionGroupsSliceName]: MissionGroupsSliceReducer,
    [configurationSliceName]: ConfigurationSliceReducer,
    [profileSliceName]: ProfileSliceReducer,
    [passwordSliceName]: PasswordSliceReducer,
    [notificationSettingsSliceName]: NotificationSettingsSliceReducer,
    [licensesSettingsSliceName]: LicensesSettingsSliceReducer,
    [globalNavSliceName]: GlobalNavReducer,
    [pilotsSliceName]: PilotsSliceReducer,
    [activitySliceName]: ActivitySliceReducer,
    [aircraftSliceName]: AircraftSliceReducer,
    [missionsTableSliceName]: MissionsTableReducer,
    [maintenanceTemplateFormSliceName]: maintenanceTemplateFormReducer,
    [currencyPolicyFormSliceName]: currencyPolicyFormReducer,
    [checklistDetailsSliceName]: ChecklistDetailsReducer,
    ...ColorFinderV2Reducer,
} as const

const bannerReducers = {
    ignoredBanners: ignoredBanners,
}

export const mapStateReducerKeys = Object.keys(mapStateReducers) as unknown as UnionToTuple<
    keyof typeof mapStateReducers
>

const reducerCollection = {
    // any non-ui related state that controls how the application behaves
    ...mapStateReducers,
    application: applicationReducers,
    // holds details for use when triggering an export of data
    exports: exportsReducer,
    // contains specific details about external systems that help govern how the ui and interaction behavior works when those system statuses change
    systems: systemsReducer,
    // @todo -- review the following:
    // old reducer that may not be used very much?
    loading: loadingReducer,
    // additional reducers
    ...PortfolioManagement,
    ...UserManagement,
    ...pubsubReducer,
    licenses: licensesSliceReducer,
    // the original way to capture/cache request data -- likely we'll transition to more of a rtk-query approach for vanilla-requests in the future
    entities: entitiesReducers,
    project: projectReducer,
    // add reducers that don't contain map state to the list, otherwise add it to the mapStateReducers object so the state will automatically get sent with pubsub
    ...PDFRasterUI,
    ...bannerReducers,
    ...PilotTrackingReducer,
    [checklistCreationSliceName]: ChecklistCreationSliceReducer,
    [overviewSliceName]: OverviewReducer,
}

const combinedReducer = combineReducers(reducerCollection)

/**
 * rather than individual slice cleanups, we clear the entire state this way.
 * individual filtering is possible if desired by grabbing the previous state parts as well and injecting them back into state
 */

const rootReducer: Reducer = (state: RootState, action: AnyAction) => {
    if (action.type === userLogoutAction.type) {
        state = {} as RootState
    }

    if (action.type === receiveState.type) {
        if (action.payload.viewState) setNewViewstate(action.payload.viewState)
        return { ...state, ...combineStates(state, action.payload.reduxState) }
    }

    if (action.type === loadProject.fulfilled) {
        const payload = action.payload as Project
        return { ...state, project: payload }
    }

    return combinedReducer(state, action)
}

export type RootState = ReturnType<typeof combinedReducer>

export default rootReducer
